import { z } from 'zod'

export const COACHING_RESOURCES_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const coachingResourceReadQuerySchema = z.object({
  orgId: optionalQueryString,
  cohort_id: optionalQueryString,
  cohortId: optionalQueryString,
  company_id: optionalQueryString,
  companyId: optionalQueryString,
  page: optionalQueryString,
  limit: optionalQueryString,
  offset: optionalQueryString,
  per_page: optionalQueryString,
}).passthrough()

export const coachingResourceSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  companyId: nullableString,
  cohortId: nullableString,
  createdByUserId: nullableString,
  title: z.string().min(1),
  description: nullableString,
  resourceType: z.string().min(1),
  url: nullableString,
  uploadId: nullableString,
  isPublic: z.boolean(),
  status: z.string().min(1),
  active: z.boolean(),
  metadata: z.record(z.unknown()),
  companyName: nullableString,
  cohortName: nullableString,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const coachingResourceReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const coachingResourceReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([
    z.literal('coaching_resources'),
    z.literal('coaching_companies'),
    z.literal('coaching_cohorts'),
  ]),
  projection: z.literal('coaching-resource-metadata'),
  rawPayloadReturned: z.literal(false),
}).passthrough()

export const coachingResourceListResponseSchema = z.object({
  contract_version: z.literal(COACHING_RESOURCES_CONTRACT_VERSION),
  resources: z.array(coachingResourceSummarySchema),
  resourceEvidence: coachingResourceReadEvidenceSchema,
  sideEffects: coachingResourceReadSideEffectsSchema,
}).passthrough()

export const coachingResourceDetailResponseSchema = z.object({
  contract_version: z.literal(COACHING_RESOURCES_CONTRACT_VERSION),
  resource: coachingResourceSummarySchema,
  resourceEvidence: coachingResourceReadEvidenceSchema,
  sideEffects: coachingResourceReadSideEffectsSchema,
}).passthrough()

export const COACHING_RESOURCE_ERROR_CODES = [
  'CO_VALIDATION_FAILED',
  'CO_PERMISSION_DENIED',
  'CO_AUTH_UNAVAILABLE',
  'CO_MEMBERSHIP_UNAVAILABLE',
  'CO_DOMAIN_UNAVAILABLE',
  'CO_LEGACY_AUTH_REMOVED',
  'CO_SEAT_CAPACITY_EXCEEDED',
  'CO_RESERVATION_EXPIRED',
  'CO_LIFECYCLE_CONFLICT',
  'CO_NOT_FOUND',
  'CO_IDEMPOTENCY_CONFLICT',
  'CO_CUTOFF_EXCEEDED',
] as const

export const coachingResourceErrorEnvelopeSchema = z.object({
  contract_version: z.literal(COACHING_RESOURCES_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(COACHING_RESOURCE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().regex(/^CO-[A-Z0-9]{8}$/i),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().uuid().optional(),
  }),
}).passthrough()

export type CoachingResourceReadQuery = z.infer<typeof coachingResourceReadQuerySchema>
export type CoachingResourceSummary = z.infer<typeof coachingResourceSummarySchema>
export type CoachingResourceReadSideEffects = z.infer<typeof coachingResourceReadSideEffectsSchema>
export type CoachingResourceReadEvidence = z.infer<typeof coachingResourceReadEvidenceSchema>
export type CoachingResourceListResponse = z.infer<typeof coachingResourceListResponseSchema>
export type CoachingResourceDetailResponse = z.infer<typeof coachingResourceDetailResponseSchema>
export type CoachingResourceErrorCode = (typeof COACHING_RESOURCE_ERROR_CODES)[number]
export type CoachingResourceErrorEnvelope = z.infer<typeof coachingResourceErrorEnvelopeSchema>
